import { NgClass } from '@angular/common';
import { Component, forwardRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    PASSWORD_AGAIN_NO_MATCH,
    PASSWORD_AGAIN_REQUIRED,
    PASSWORD_REQUIRED,
    PASSWORD_TOO_SHORT,
} from '@config/error-messages.const';
import { CtrlErrorMessageDirective } from '@co-3/ctrl-ui/forms';
import { ReactiveLabelDirective } from 'app/shared/directives/reactive-label.directive';
import { mustMatch } from 'app/shared/validators/must-match.validator';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrl: './change-password.component.scss',
    imports: [ReactiveFormsModule, ReactiveLabelDirective, NgClass, CtrlErrorMessageDirective],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChangePasswordComponent),
            multi: true,
        },
    ],
})
export class ChangePasswordComponent implements ControlValueAccessor {
    protected onChange?: (val: string | null) => void;
    protected onTouched?: () => void;
    public form: FormGroup;
    protected changePWDisabled = false;

    protected passwordErrorMessages = {
        required: PASSWORD_REQUIRED,
        minlength: PASSWORD_TOO_SHORT,
    };
    protected passwordAgainErrorMessages = {
        required: PASSWORD_AGAIN_REQUIRED,
        minlength: PASSWORD_TOO_SHORT,
        mustMatch: PASSWORD_AGAIN_NO_MATCH,
    };

    public constructor() {
        this.form = new FormGroup(
            {
                password: new FormControl('', [Validators.minLength(8), Validators.required]),
                passwordAgain: new FormControl('', [Validators.minLength(8), Validators.required]),
            },
            mustMatch('password', 'passwordAgain'),
        );
        this.subscribeToFormChange();
    }

    public setDisabledState(isDisabled: boolean): void {
        this.changePWDisabled = isDisabled;
        if (isDisabled) {
            this.form.get('password').disable();
            this.form.get('passwordAgain').disable();
        } else {
            this.form.get('password').enable();
            this.form.get('passwordAgain').enable();
        }
    }

    private subscribeToFormChange(): void {
        this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
            if (typeof this.onChange === 'function') {
                if (value.password && value.password === value.passwordAgain) {
                    this.onChange(value.password);
                } else {
                    this.onChange(null);
                }
            }
        });
    }

    public writeValue(value: string | null): void {
        if (value === '') {
            this.form.reset();
        }
    }

    public registerOnChange(fn: (val: string | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
}
